import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../App';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Building2, Truck, Box, UserCircle } from 'lucide-react';

// Hooks
import { useTransportistas } from './hooks';

// Components
import {
  CompaniesTab,
  TrucksTab,
  TrailersTab,
  DriversTab,
  BulkUploadModal,
  AssignAdminModal
} from './components';

/**
 * Página principal de Transportistas - Versión Refactorizada
 * 
 * Permisos por rol:
 * - MASTER/ADMIN: Ve todas las empresas y puede gestionar todo
 * - TRANSPORTISTA: Solo ve sus propias empresas y entidades asociadas
 */
const Transportistas = () => {
  const { user: currentUser } = useAuth();
  
  const {
    companies,
    trucks,
    trailers,
    drivers,
    users,
    admins,
    loading,
    isTransportista,
    refreshCompanies,
    refreshTrucks,
    refreshTrailers,
    refreshDrivers,
    fetchData,
    getCompanyName
  } = useTransportistas(currentUser);

  // Modal states
  const [bulkUploadModal, setBulkUploadModal] = useState({ open: false, entityType: null });
  const [assignModal, setAssignModal] = useState({ open: false, entityType: null });

  const openBulkUpload = (entityType) => setBulkUploadModal({ open: true, entityType });
  const closeBulkUpload = () => setBulkUploadModal({ open: false, entityType: null });

  const openAssign = (entityType) => setAssignModal({ open: true, entityType });
  const closeAssign = () => setAssignModal({ open: false, entityType: null });

  // Get entities based on type for assign modal
  const getEntitiesForAssign = () => {
    switch (assignModal.entityType) {
      case 'companies': return companies;
      case 'trucks': return trucks;
      case 'trailers': return trailers;
      case 'drivers': return drivers;
      default: return [];
    }
  };

  // Get existing entities for bulk upload validation
  const getExistingEntities = () => {
    switch (bulkUploadModal.entityType) {
      case 'companies': return companies;
      case 'trucks': return trucks;
      case 'trailers': return trailers;
      case 'drivers': return drivers;
      default: return [];
    }
  };

  // Get refresh function based on entity type
  const getRefreshFunction = (entityType) => {
    switch (entityType) {
      case 'companies': return refreshCompanies;
      case 'trucks': return refreshTrucks;
      case 'trailers': return refreshTrailers;
      case 'drivers': return refreshDrivers;
      default: return fetchData;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="transportistas-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">
              {isTransportista ? 'Mi Flota' : 'Transportistas'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isTransportista 
                ? 'Gestiona tus empresas, camiones, acoplados y choferes'
                : 'Gestión de empresas, camiones, acoplados y choferes'
              }
            </p>
          </div>
        </div>

        <Tabs defaultValue="empresas" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-slate-100 p-1 rounded-lg">
            <TabsTrigger value="empresas" data-testid="tab-empresas" className="flex items-center gap-2">
              <Building2 size={16} />
              <span>Empresas</span>
            </TabsTrigger>
            <TabsTrigger value="camiones" data-testid="tab-camiones" className="flex items-center gap-2">
              <Truck size={16} />
              <span>Camiones</span>
            </TabsTrigger>
            <TabsTrigger value="acoplados" data-testid="tab-acoplados" className="flex items-center gap-2">
              <Box size={16} />
              <span>Acoplados</span>
            </TabsTrigger>
            <TabsTrigger value="choferes" data-testid="tab-choferes" className="flex items-center gap-2">
              <UserCircle size={16} />
              <span>Choferes</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="empresas" className="mt-6">
            <CompaniesTab 
              companies={companies} 
              users={users} 
              admins={admins}
              onRefresh={refreshCompanies}
              onBulkUpload={openBulkUpload}
              onAssign={openAssign}
              isTransportista={isTransportista}
              currentUser={currentUser}
            />
          </TabsContent>

          <TabsContent value="camiones" className="mt-6">
            <TrucksTab 
              trucks={trucks} 
              companies={companies}
              onRefresh={refreshTrucks}
              onBulkUpload={openBulkUpload}
              onAssign={openAssign}
              getCompanyName={getCompanyName}
              isTransportista={isTransportista}
            />
          </TabsContent>

          <TabsContent value="acoplados" className="mt-6">
            <TrailersTab 
              trailers={trailers} 
              companies={companies}
              onRefresh={refreshTrailers}
              onBulkUpload={openBulkUpload}
              onAssign={openAssign}
              getCompanyName={getCompanyName}
              isTransportista={isTransportista}
            />
          </TabsContent>

          <TabsContent value="choferes" className="mt-6">
            <DriversTab 
              drivers={drivers} 
              companies={companies}
              onRefresh={refreshDrivers}
              onBulkUpload={openBulkUpload}
              onAssign={openAssign}
              getCompanyName={getCompanyName}
              isTransportista={isTransportista}
            />
          </TabsContent>
        </Tabs>

        {/* Bulk Upload Modal - Solo para MASTER */}
        {!isTransportista && (
          <BulkUploadModal
            isOpen={bulkUploadModal.open}
            onClose={closeBulkUpload}
            entityType={bulkUploadModal.entityType}
            companies={companies}
            existingEntities={getExistingEntities()}
            onSuccess={() => getRefreshFunction(bulkUploadModal.entityType)()}
          />
        )}

        {/* Assign Admin Modal - Solo para MASTER */}
        {!isTransportista && (
          <AssignAdminModal
            isOpen={assignModal.open}
            onClose={closeAssign}
            entityType={assignModal.entityType}
            entities={getEntitiesForAssign()}
            admins={admins}
            companies={companies}
            onSuccess={fetchData}
          />
        )}
      </div>
    </Layout>
  );
};

export default Transportistas;
